import { useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useEventTimezone } from "@/providers/EventProvider";
import { safeDisplayTimeZone } from "@/lib/utils/datetime-tz";
import { parseDateTimeString } from "@/lib/utils/parser";

/** One temporary refusal, as `sent_emails.softBounces` stores it. */
export interface SoftBounceEntry {
  at?: string;
  subType?: string;
}

/**
 * The tooltip for the amber "temporarily deferred" mark, shared by every
 * surface that shows it.
 *
 * <p>It exists as a hook because the mark now appears in two places — the
 * sent-mails table and the guest-detail list — and those two already keep
 * SEPARATE copies of the status badge colours, which is how `SKIPPED` ended up
 * styled in one and unstyled in the other. A third hand-copy of the formatting
 * was not worth the symmetry.
 *
 * <p>Two rules the naive version got wrong, both worth stating:
 *
 * <ul>
 *   <li><b>Sort, do not trust the order.</b> The parser writes these with
 *       `$addToSet`, so the array is a SET — its order carries no meaning and
 *       "the last element" is not "the most recent deferral".</li>
 *   <li><b>Event timezone, app locale.</b> `toLocaleString()` takes the
 *       browser's of both. The `sentAt` column beside this mark renders in the
 *       EVENT's zone (TCCDateTime), and a deferral is read against the send
 *       time — two zones side by side is a misreading waiting to happen. Every
 *       other date and number in this cockpit follows the app locale, not the
 *       machine's.</li>
 * </ul>
 *
 * @returns the tooltip text, or `undefined` when there is nothing to mark.
 */
export function useSoftBounceTooltip(
  softBounces?: SoftBounceEntry[] | null,
): string | undefined {
  const tSoft = useTranslations("softBounce");
  const t = useTranslations("optionsMultiSingleSelect");
  const locale = useLocale();
  const tz = safeDisplayTimeZone(useEventTimezone());

  return useMemo(() => {
    if (!softBounces?.length) return undefined;
    const times = softBounces
      .map((b) => (b?.at ? new Date(b.at) : null))
      .filter((d): d is Date => d != null && !Number.isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    const last = times[times.length - 1];
    return last
      ? tSoft("tooltip", {
          count: softBounces.length,
          last: parseDateTimeString(last, t("clock"), locale, tz, true),
        })
      : // Every entry's time was unusable. The COUNT is still a fact worth
        // showing — an entry exists because a deferral happened.
        tSoft("tooltipNoTime", { count: softBounces.length });
  }, [softBounces, tSoft, t, locale, tz]);
}
